<template>
  <div
    class="fill-height"
    style="width:100%"
  >
    <v-navigation-drawer
      v-model="drawer"
      :clipped="$vuetify.breakpoint.lgAndUp"
      app
    >
      <v-list dense>
        <template v-for="(item, i) in sidebar">
          <v-row
            v-if="item.heading"
            :key="`${i}-${item.heading}`"
            align="center"
          >
            <v-col cols="12">
              <v-subheader
                v-if="item.heading"
                class="justify-center"
              >
                {{ item.heading }}
              </v-subheader>
            </v-col>
          </v-row>
          <v-list-group
            v-else-if="item.children"
            :key="item.text"
            v-model="item.model"
            append-icon=""
          >
            <template v-slot:activator>
              <v-list-item style="padding:0;">
                <v-list-item-action>
                  <v-icon>{{ item.model ? item.icon : item['icon-alt'] }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.text }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
            <v-list-item
              v-for="(child, j) in item.children"
              :key="`${j}-${child}`"
              :to="child.link"
            >
              <v-list-item-action v-if="child.icon">
                <v-icon>{{ child.icon }}</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ child.text }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-group>
          <v-list-item
            v-else
            :key="item.text"
            :to="item.link"
          >
            <v-list-item-action>
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                {{ item.text }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar
      :clipped-left="$vuetify.breakpoint.lgAndUp"
      app
      color="secondary"
      dark
    >
      <v-app-bar-nav-icon @click.stop="drawer = !drawer" />
      <v-toolbar-title class="pl-2">
        <span class="hidden-sm-and-down">{{ $vuetify.lang.t('$vuetify.dataCube') }}</span>
      </v-toolbar-title>
      <div class="flex-grow-1" />
      <v-btn
        icon
        large
        to="/preferences"
      >
        <v-avatar
          size="32px"
          item
        >
          <v-icon>mdi-account-circle</v-icon>
        </v-avatar>
      </v-btn>
      <Logout />
    </v-app-bar>
    <v-content
      class="fill-height"
      style="overflow:hidden;"
    >
      <SnackbarComponent />
      <v-container
        class="fill-height mt-5"
        fluid
      >
        <v-row
          align="center"
          justify="center"
          class="px-3 fill-height"
          style="max-width: initial;"
        >
          <v-col
            cols="12"
            sm="12"
            md="12"
            class="fill-height"
          >
            <div v-show="!globalLoading">
              <slot />
            </div>
            <div
              v-show="globalLoading"
              class="justify-center align-center fill-height"
              style="display:flex;"
            >
              <v-progress-circular
                :size="75"
                color="primary"
                indeterminate
              />
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-content>
  </div>
</template>

<script>
import Logout from '../components/Logout.vue';
import SnackbarComponent from '../components/SnackbarComponent.vue';

export default {
  components: {
    Logout,
    SnackbarComponent,
  },
  data: () => {
    return {
      drawer: null,
      sidebar: null,
    };
  },
  computed: {
    globalLoading() {
      return this.$store.state.globalLoading;
    },
  },
  created() {
    // Warning: avoid using computed property for the sidebar (vuetify limitations).
    const sidebar = [];
    const general = [];
    const features = [];
    const views = [];
    const settings = [];

    // General
    if (this.$store.state.user.permissions.indexOf(2) >= 0) {
      general.push(
        { icon: 'mdi-robot-industrial', text: this.$vuetify.lang.t('$vuetify.sidebar.machine'), link: '/machine' },
      );
    }
    if (this.$store.state.user.permissions.indexOf(3) >= 0) {
      general.push(
        { icon: 'mdi-chip', text: this.$vuetify.lang.t('$vuetify.sidebar.dataController'), link: '/datacontroller' },
      );
    }

    // Features
    if (this.$store.state.user.permissions.indexOf(6) >= 0) {
      features.push(
        {
          icon: 'mdi-chevron-up',
          'icon-alt': 'mdi-chevron-down',
          text: this.$vuetify.lang.t('$vuetify.sidebar.weavingMachine.title'),
          model: false,
          children: [
            { icon: 'mdi-cog', text: this.$vuetify.lang.t('$vuetify.sidebar.weavingMachine.settings'), link: '/weaving-machine/settings' },
            { icon: 'mdi-chart-bar', text: this.$vuetify.lang.t('$vuetify.sidebar.weavingMachine.statistics'), link: '/weaving-machine/statistics' },
          ],
        },
      );
    }

    if (this.$store.state.user.permissions.indexOf(7) >= 0) {
      features.push(
        {
          icon: 'mdi-chevron-up',
          'icon-alt': 'mdi-chevron-down',
          text: this.$vuetify.lang.t('$vuetify.sidebar.machineStats.title'),
          model: false,
          children: [
            { icon: 'mdi-monitor-dashboard', text: this.$vuetify.lang.t('$vuetify.sidebar.machineStats.overview'), link: '/stat/overview' },
            { icon: 'mdi-file-table-box-multiple', text: this.$vuetify.lang.t('$vuetify.sidebar.machineStats.summarize'), link: '/stat/summarize' },
            { icon: 'mdi-chart-areaspline-variant', text: this.$vuetify.lang.t('$vuetify.sidebar.machineStats.statistics'), link: '/stat/graph' },
            { icon: 'mdi-timer', text: this.$vuetify.lang.t('$vuetify.sidebar.machineStats.realtime'), link: '/stat/graph-realtime' },
          ],
        },
      );
    }

    if (this.$store.state.user.permissions.indexOf(9) >= 0) {
      features.push(
        {
          icon: 'mdi-chevron-up',
          'icon-alt': 'mdi-chevron-down',
          text: this.$vuetify.lang.t('$vuetify.sidebar.annotations.title'),
          model: false,
          children: [
            { icon: 'mdi-cog', text: this.$vuetify.lang.t('$vuetify.sidebar.annotations.settings'), link: '/annotation/settings' },
            { icon: 'mdi-file-table-box-multiple', text: this.$vuetify.lang.t('$vuetify.sidebar.annotations.form'), link: '/annotation/form' },
          ],
        },
      );
    }

    // Views
    if (this.$store.state.user.permissions.indexOf(6) >= 0 || this.$store.state.user.permissions.indexOf(7) >= 0) {
      const data = {
        icon: 'mdi-chevron-up',
        'icon-alt': 'mdi-chevron-down',
        text: this.$vuetify.lang.t('$vuetify.sidebar.fullscreenStatistics.title'),
        model: false,
        children: [],
      };
      if (this.$store.state.user.permissions.indexOf(6) >= 0) {
        data.children.push({ icon: 'mdi-chart-bar', text: this.$vuetify.lang.t('$vuetify.sidebar.fullscreenStatistics.weavingMachine'), link: '/full-views/weaving-machine/statistics' });
      }
      if (this.$store.state.user.permissions.indexOf(7) >= 0) {
        data.children.push({ icon: 'mdi-monitor-dashboard', text: this.$vuetify.lang.t('$vuetify.sidebar.fullscreenStatistics.overview'), link: '/full-views/stat/overview' });
      }
      views.push(data);
    }

    // Settings
    if (this.$store.state.user.permissions.indexOf(1) >= 0 || this.$store.state.user.permissions.indexOf(5) >= 0) {
      const data = {
        icon: 'mdi-chevron-up',
        'icon-alt': 'mdi-chevron-down',
        text: this.$vuetify.lang.t('$vuetify.sidebar.management.title'),
        model: false,
        children: [],
      };
      if (this.$store.state.user.permissions.indexOf(1) >= 0) {
        data.children.push({ icon: 'mdi-account-multiple', text: this.$vuetify.lang.t('$vuetify.sidebar.management.users'), link: '/manage/user' });
      }
      if (this.$store.state.user.permissions.indexOf(5) >= 0) {
        data.children.push({ icon: 'mdi-account-group', text: this.$vuetify.lang.t('$vuetify.sidebar.management.groups'), link: '/manage/group' });
      }
      settings.push(data);
    }

    sidebar.push(
      { heading: this.$vuetify.lang.t('$vuetify.sidebar.dashboard') },
      { icon: 'mdi-monitor-dashboard', text: this.$vuetify.lang.t('$vuetify.sidebar.overview'), link: '/' },
    );
    if (general.length) {
      sidebar.push({ heading: this.$vuetify.lang.t('$vuetify.sidebar.general') });
      sidebar.push(...general);
    }
    if (features.length) {
      sidebar.push({ heading: this.$vuetify.lang.t('$vuetify.sidebar.features') });
      sidebar.push(...features);
    }
    if (views.length) {
      sidebar.push({ heading: this.$vuetify.lang.t('$vuetify.sidebar.views') });
      sidebar.push(...views);
    }
    if (settings.length) {
      sidebar.push({ heading: this.$vuetify.lang.t('$vuetify.sidebar.settings') });
      sidebar.push(...settings);
    }
    sidebar.push(
      { heading: this.$vuetify.lang.t('$vuetify.sidebar.support') },
      { icon: 'mdi-cog', text: this.$vuetify.lang.t('$vuetify.sidebar.preferences'), link: '/preferences' },
      { icon: 'mdi-help', text: this.$vuetify.lang.t('$vuetify.sidebar.help'), link: '' },
    );
    this.sidebar = sidebar;
  },
};
</script>

<style lang="scss">
@import '~vuetify/src/styles/settings/_variables';

@mixin breakpoint($media){
  @media #{map-get($display-breakpoints, $media)} {
    @content;
  }
}

.v-dialog{
  max-width: 75vw;
}

.table__top-header{
  display: flex;
  align-items: center;
  justify-content: center;

  @include breakpoint('sm-and-up'){
    justify-content: flex-start;
  }

  .v-divider{
    display: none;

    @include breakpoint('sm-and-up'){
      display: inline-flex;
    }
  }

}
.table__top-tools {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-direction: column;

  @include breakpoint('sm-and-up'){
    flex-direction: initial;
  }

  .v-text-field{
    margin-top: 0;
    padding-top: 0;
  }

  .v-input,
  .v-btn,
  .download-group{
    margin: 8px;

    @include breakpoint('sm-and-up'){
      margin: 0 5px;
    }
  }
}

.buttons__container{

  @include breakpoint('sm-and-up'){
    display: flex;
    justify-content: flex-end;
    margin-bottom: 5px;
  }

  .buttons__mode{
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
    @include breakpoint('sm-and-up'){
      flex-direction: initial;
      margin: 0 20px;
    }
  }

  .buttons__time{
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
    @include breakpoint('sm-and-up'){
      flex-direction: initial;
      margin-bottom: 0;
    }
  }
}


</style>
